/**
 * Danger-zone control: wipe operational tenant data, keep identity/billing/COA.
 */

import React, { useState } from 'react';
import { Icons } from '../common/Icons';
import { tenantsService } from '../../services/api';

function countLines(obj) {
  if (!obj) return [];
  return Object.entries(obj)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

const TenantResetPanel = ({ tenant, onReset }) => {
  const [preview, setPreview] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const nameOk = preview && confirmName.trim() === String(preview.tenant_name || '').trim();
  const canExecute = nameOk && totpCode.trim().length >= 6 && !busy;

  const loadPreview = async () => {
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      const res = await tenantsService.previewResetData(tenant.id);
      setPreview(res.data || res);
      setConfirmName('');
      setTotpCode('');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not preview reset.');
    } finally {
      setBusy(false);
    }
  };

  const execute = async () => {
    if (!canExecute) return;
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      const res = await tenantsService.resetData(tenant.id, {
        confirm_name: confirmName.trim(),
        totp_code: totpCode.trim(),
      });
      const data = res.data || res;
      setPreview(data);
      setConfirmName('');
      setTotpCode('');
      setSuccess(
        data.row_count
          ? `Removed ${data.row_count} operational row${data.row_count === 1 ? '' : 's'}. Users, subscription, and chart of accounts were kept.`
          : 'No operational rows to remove. Tenant is already a clean shell.'
      );
      if (onReset) onReset();
    } catch (err) {
      const body = err.response?.data;
      setError(body?.message || 'Reset failed.');
    } finally {
      setBusy(false);
    }
  };

  const rows = countLines(preview && (preview.deleted || preview.will_delete));
  const kept = (preview && preview.kept) || [];

  return (
    <div
      className="card"
      style={{
        marginTop: 24,
        borderColor: 'var(--error, #b91c1c)',
        borderWidth: 1,
      }}
    >
      <h3 className="card-title" style={{ color: 'var(--error, #b91c1c)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icons.AlertCircle size={20} />
        Reset tenant data
      </h3>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>
        Puts this tenant back to a newly onboarded shell: no customers, jobs, invoices,
        inventory, or books activity. Keeps the tenant identity, users, subscription,
        Stripe Connect, shop/site settings, and chart of accounts. This cannot be undone
        from System Admin.
      </p>

      {error && (
        <div className="alert alert-error mb-2" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Icons.AlertCircle size={16} /> {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success mb-2" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Icons.CheckCircle size={16} /> {success}
        </div>
      )}

      {!preview && (
        <button className="btn btn-secondary" onClick={loadPreview} disabled={busy}>
          {busy ? <><Icons.Loader size={16} className="spin" /> Checking…</> : 'Preview what would be removed'}
        </button>
      )}

      {preview && (
        <div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
            {preview.row_count
              ? `${preview.row_count} row${preview.row_count === 1 ? '' : 's'} across ${rows.length} table${rows.length === 1 ? '' : 's'}`
              : 'No operational rows found'}
          </div>
          {rows.length > 0 && (
            <ul style={{ fontSize: 13, margin: '0 0 12px', paddingLeft: 18, maxHeight: 180, overflow: 'auto' }}>
              {rows.map(([table, n]) => (
                <li key={table}><code>{table}</code> — {n}</li>
              ))}
            </ul>
          )}
          {kept.length > 0 && (
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
              Kept: {kept.join(', ')}
            </p>
          )}
          {(preview.warnings || []).map((w) => (
            <p key={w.code} style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
              {w.message}
            </p>
          ))}

          {!preview.dry_run ? (
            <button className="btn btn-secondary" onClick={loadPreview} disabled={busy}>
              Preview again
            </button>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Type the tenant name to confirm ({preview.tenant_name})</label>
                <input
                  className="form-control"
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Your TOTP code</label>
                <input
                  className="form-control"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="6-digit code"
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" onClick={() => { setPreview(null); setError(null); }} disabled={busy}>
                  Cancel
                </button>
                <button className="btn btn-danger" onClick={execute} disabled={!canExecute}>
                  {busy ? <><Icons.Loader size={16} className="spin" /> Resetting…</> : 'Reset tenant data'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TenantResetPanel;
